import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  DELETE_CHANGE_TYPES,
  selectSubmittalFeatureLogs
} from "pages/submittal-log/hooks/submittal-log-data";
import { useContext, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { QUERY_SUBMITTAL_CATEGORY_COUNTS } from "services/graphQL/ciq-queries";
import { TAuditChangeLog } from "change-events/change-event-polling";

type AggregateCount = { aggregate: { count: number } | null } | null;

type SubmittalCategoryCountsResponse = {
  tracked: AggregateCount;
  untracked: AggregateCount;
  void: AggregateCount;
  deleted_from_source: AggregateCount;
};

const CREATE_CHANGE_TYPES = new Set(["insert", "create"]);

const SUBMITTAL_IMPORT_TYPES = new Set(["ACC_IMPORT", "PROCORE_IMPORT"]);

const isSubmittalInstanceLog = (log: TAuditChangeLog) => {
  if (log.data_source !== "feature_instance") return false;
  if (selectSubmittalFeatureLogs([log]).length) return true;
  const instanceIds = new Set(log.info?.feature_instance_ids ?? []);
  return (
    log.info?.linked_features?.some(
      (feature) =>
        feature.featureType === "SUBMITTALS" && instanceIds.has(feature.id)
    ) ?? false
  );
};

const hasSubmittalCreateOrDelete = (logs: TAuditChangeLog[]) =>
  logs.some(
    (log) =>
      (CREATE_CHANGE_TYPES.has(log.change_type) ||
        DELETE_CHANGE_TYPES.has(log.change_type)) &&
      isSubmittalInstanceLog(log)
  );

const hasProcessedSubmittalImport = (
  logs: TAuditChangeLog[],
  projectId: string
) =>
  logs.some(
    (log) =>
      log.data_source === "import_log" &&
      SUBMITTAL_IMPORT_TYPES.has(log.info?.import_type ?? "") &&
      log.info?.status === "PROCESSED" &&
      log.project_id === projectId
  );

export type SubmittalCategoryCounts = {
  tracked?: number;
  untracked?: number;
  voided?: number;
  deleted?: number;
};

function useSubmittalCategoryCounts(): SubmittalCategoryCounts {
  const { projectId } = useParams() as { projectId: string };
  const { gqlClientForProject, eventLogs } = useContext(ProjectContext);

  const { data, refetch } = useCIQQuery<SubmittalCategoryCountsResponse>(
    QUERY_SUBMITTAL_CATEGORY_COUNTS,
    {
      client: gqlClientForProject,
      skip: !gqlClientForProject
    }
  );

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      if (
        hasSubmittalCreateOrDelete(eventLogs) ||
        hasProcessedSubmittalImport(eventLogs, projectId)
      ) {
        refetch?.()?.catch?.(() => {});
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, projectId, refetch]);

  return useMemo(
    () => ({
      tracked: data?.tracked?.aggregate?.count,
      untracked: data?.untracked?.aggregate?.count,
      voided: data?.void?.aggregate?.count,
      deleted: data?.deleted_from_source?.aggregate?.count
    }),
    [data]
  );
}

export default useSubmittalCategoryCounts;
